import { memoizeWith, pick,values } from 'ramda'
import { CSSProperties } from 'react'
import { useSelector } from 'react-redux'
import { EmbedTheme } from 'types/embed'

const DEFAULT_THEME = {
  textColor: '353f45',
  backgroundColor: '0071EB',
  foregroundColor: 'ffffff',
  widgetBackgroundColor: '0071EB',
  activeColor: 'blue'
}

const resolveBackgroundStyles = (theme: EmbedTheme): CSSProperties => {
  return {
    background: `#${theme.backgroundColor}`,
    color: `#${theme.textColor}`
  }
}

const resolveSvgStyles = (theme: EmbedTheme): CSSProperties => {
  return {
    fill: `#${theme.textColor}`
  }
}

const resolveBorderStyles = (theme: EmbedTheme): CSSProperties => {
  return {
    borderColor: `#${theme.backgroundColor}`
  }
}

const resolveTextColorStyles = (theme: EmbedTheme): CSSProperties => {
  return {
    color: `#${theme.foregroundColor}`
  }
}

const resolveInlineImageStyles = (theme: EmbedTheme): CSSProperties => {
  return {
    background: `#${theme.foregroundColor}`,
    border: `4px solid #${theme.foregroundColor}`
  }
}

const resolveHeaderTextStyles = (theme: EmbedTheme): CSSProperties => {
  return {
    color: `#${theme.foregroundColor}`
  }
}

const resolveInlineFileStyles = (theme: EmbedTheme): CSSProperties => {
  return {
    color: `#${theme.foregroundColor}`
  }
}

const resolveButtonStyles = (theme: EmbedTheme): CSSProperties => {
  return {
    color: `#${theme.backgroundColor}`,
    borderColor: `#${theme.backgroundColor}`,
    backgroundColor: `#${theme.foregroundColor}`
  }
}

const resolveForegroundColors = (theme: EmbedTheme): CSSProperties => {
  return {
    backgroundColor: `#${theme.backgroundColor}`,
    color: `#${theme.foregroundColor}`,
    border: `1px solid #${theme.backgroundColor}`
  }
}

const resolvePrimaryTextColor = (theme: EmbedTheme): CSSProperties => {
  return {
    color: `#${theme.backgroundColor}`
  }
}

const resolveWidgetBackgroundStyles = (theme: EmbedTheme): CSSProperties => {
  return {
    background: `#${theme.widgetBackgroundColor}`
  }
}

const resolveWidgetAlphaBackground = (theme: EmbedTheme, alpha = '.8'): CSSProperties => {
  const convertHex = (hex) => {
    const r = parseInt(hex.substring(0,2), 16)
    const g = parseInt(hex.substring(2,4), 16)
    const b = parseInt(hex.substring(4,6), 16)

    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  return {
    background: convertHex(theme.widgetBackgroundColor)
  }
}

const resolveDefaultBotAvatarBackground = (theme: EmbedTheme): CSSProperties => {
  return {
    background: `#${theme.backgroundColor}`
  }
}

const pickThemeColors = pick(['activeColor', 'foregroundColor', 'backgroundColor', 'textColor', 'widgetBackgroundColor'])
const memoIdentity = (theme: EmbedTheme) => values(pickThemeColors(theme)).join(';')

const getThemeColors = memoizeWith(memoIdentity, (userTheme: EmbedTheme) => {
  const theme = {
    ...DEFAULT_THEME,
    ...userTheme,
  }

  return {
    backgroundStyles: resolveBackgroundStyles(theme),
    borderStyles: resolveBorderStyles(theme),
    svgStyles: resolveSvgStyles(theme),
    textColorStyles: resolveTextColorStyles(theme),
    buttonStyles: resolveButtonStyles(theme),
    foregroundStyles: resolveForegroundColors(theme),
    inlineImageStyles: resolveInlineImageStyles(theme),
    widgetBackgroundStyles: resolveWidgetBackgroundStyles(theme),
    conversationHeaderTextStyles: resolveHeaderTextStyles(theme),
    inlineFileStyles: resolveInlineFileStyles(theme),
    defaultBotAvatarBackgroundStyles: resolveDefaultBotAvatarBackground(theme),

    activeColor: theme.activeColor,
    widgetSecondaryColor: `#${theme.foregroundColor}`,
    widgetBackgroundColor: theme.widgetBackgroundColor,
    widgetBackgroundAlpha: resolveWidgetAlphaBackground(theme),
    primaryTextColor: resolvePrimaryTextColor(theme)
  }
})

const useThemeColors = () => {
  const userTheme: EmbedTheme = useSelector(state => state.embed.configuration.theme)
  return getThemeColors(userTheme)
}

export default useThemeColors
